import React, { useEffect, useState } from 'react';
import SystemDashboard from 'layouts/SystemDashboard';
import { Box } from '@mui/material';
import { getProduct, saveProduct, updateProduct } from './ProductService';
import { useNavigate, useParams } from 'react-router-dom';
import Container from 'components/Container';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ProductAppList from './ProductAppList';
import { notify } from 'utils/notify';
import { extractErrorMessage } from 'utils/extractErrorMessage';

const ProductDetail = (): JSX.Element => {
  const [productType, setProductType] = useState('');
  const [productName, setProductName] = useState('');
  const [description, setDescription] = useState('');
  const [maxAllowedUsers, setMaxAllowedUsers] = useState('');
  const [maxAllowedStorage, setMaxAllowedStorage] = useState('');
  const [productCost, setProductCost] = useState('');
  const [costFrequencyValue, setCostFrequencyValue] = useState('');
  const [costFrequencyUnit, setCostFrequencyUnit] = useState('');
  const [status, setStatus] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [trial, setIsTrial] = useState(false);
  const [accountType, setAccountType] = useState('PERSONAL');
  const [maxAllowedModes, setMaxAllowedModes] = useState('');
  const [maxAllowedProgrammes, setMaxAllowedProgrammes] = useState('');

  const { productid } = useParams();
  const navigate = useNavigate();

  function clearProductDetailsForm() {
    setProductType('');
    setProductName('');
    setDescription('');
    setMaxAllowedUsers('');
    setMaxAllowedStorage('');
    setProductCost('');
    setCostFrequencyValue('');
    setCostFrequencyUnit('');
    setStatus('');
    setEnabled(false);
    setIsTrial(false);
    setAccountType('PERSONAL');
    setMaxAllowedModes('');
    setMaxAllowedProgrammes('');
  }

  const cancelSaveOrUpdateProduct = () => {
    clearProductDetailsForm();
    navigate('/system/dashboard/products');
  };

  function saveOrUpdateProduct(e) {
    e.preventDefault();
    const product = {
      productType,
      productName,
      description,
      maxAllowedUsers,
      maxAllowedStorage,
      productCost,
      costFrequencyValue,
      costFrequencyUnit,
      status,
      enabled,
      trial,
      accountType,
      maxAllowedModes,
      maxAllowedProgrammes,
    };

    if (productid) {
      updateProduct(productid, product)
        .then(() => {
          navigate('/system/dashboard/products');
        })
        .catch((error) => {
          console.error(error);
          notify(extractErrorMessage(error));
        });
    } else {
      saveProduct(product)
        .then(() => {
          navigate('/system/dashboard/products');
        })
        .catch((error) => {
          console.error(error);
          notify(extractErrorMessage(error));
        });
    }
  }

  useEffect(() => {
    if (productid) {
      getProduct(productid)
        .then((response) => {
          setProductType(response.data.productType);
          setProductName(response.data.productName);
          setDescription(response.data.description);
          setMaxAllowedUsers(response.data.maxAllowedUsers);
          setMaxAllowedStorage(response.data.maxAllowedStorage);
          setProductCost(response.data.productCost);
          setCostFrequencyValue(response.data.costFrequencyValue);
          setCostFrequencyUnit(response.data.costFrequencyUnit);
          setStatus(response.data.status);
          setEnabled(response.data.enabled);
          setIsTrial(response.data.trial);
          setAccountType(response.data.accountType || 'PERSONAL');
          setMaxAllowedModes(response.data.maxAllowedModes ?? '');
          setMaxAllowedProgrammes(response.data.maxAllowedProgrammes ?? '');
        })
        .catch((error) => {
          console.error(error);
          notify(extractErrorMessage(error));
        });
    }
  }, [productid]);

  return (
    <Container>
      <SystemDashboard>
        <Box>
          <form>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Product Type
                </Typography>
                <TextField
                  fullWidth
                  value={productType}
                  onChange={(e) => setProductType(e.target.value)}
                  placeholder="Enter product type"
                />
              </Grid>

              {/* Product Name */}
              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Product Name
                </Typography>
                <TextField
                  fullWidth
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder="Enter product name"
                />
              </Grid>

              {/* Account Type */}
              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Account Type
                </Typography>
                <FormControl fullWidth>
                  <InputLabel id="account-type-label">Account Type</InputLabel>
                  <Select
                    labelId="account-type-label"
                    value={accountType}
                    label="Account Type"
                    onChange={(e) => setAccountType(e.target.value)}
                  >
                    <MenuItem value="PERSONAL">Personal</MenuItem>
                    <MenuItem value="BUSINESS">Business</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Description
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter product description"
                />
              </Grid>

              {/* Max Allowed Users */}
              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Max Allowed Users
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={maxAllowedUsers}
                  onChange={(e) => setMaxAllowedUsers(e.target.value)}
                  placeholder="Enter max allowed users"
                />
              </Grid>

              {/* Max Allowed Storage */}
              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Max Allowed Storage (MB)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={maxAllowedStorage}
                  onChange={(e) => setMaxAllowedStorage(e.target.value)}
                  placeholder="Enter max allowed storage"
                />
              </Grid>

              {/* Max Allowed Modes */}
              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Max Allowed Modes
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={maxAllowedModes}
                  onChange={(e) => setMaxAllowedModes(e.target.value)}
                  placeholder="e.g. 3"
                />
              </Grid>

              {/* Max Allowed Programmes */}
              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Max Allowed Programmes
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={maxAllowedProgrammes}
                  onChange={(e) => setMaxAllowedProgrammes(e.target.value)}
                  placeholder="e.g. 5"
                />
              </Grid>

              {/* Product Cost */}
              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Product Cost
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={productCost}
                  onChange={(e) => setProductCost(e.target.value)}
                  placeholder="Enter product cost"
                />
              </Grid>

              {/* Cost Frequency Value */}
              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Cost Frequency Value
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={costFrequencyValue}
                  onChange={(e) => setCostFrequencyValue(e.target.value)}
                  placeholder="Enter cost frequency value"
                />
              </Grid>

              {/* Cost Frequency Unit */}
              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Cost Frequency Unit
                </Typography>
                <TextField
                  fullWidth
                  value={costFrequencyUnit}
                  onChange={(e) => setCostFrequencyUnit(e.target.value)}
                  placeholder="Enter cost frequency unit (e.g., Month/Year)"
                />
              </Grid>

              {/* Status */}
              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Status
                </Typography>
                <TextField
                  fullWidth
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  placeholder="Enter product status"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Enabled?
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={enabled}
                      onChange={(event) => setEnabled(event.target.checked)}
                      name="enabled"
                    />
                  }
                  label="Enabled"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Trial?
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={trial}
                      onChange={(event) => setIsTrial(event.target.checked)}
                      name="trial"
                    />
                  }
                  label="Trial"
                />
              </Grid>

              <Grid item container xs={12}>
                <Box
                  display="flex"
                  justifyContent={'space-between'}
                  width={1}
                  marginBottom={4}
                >
                  <Button onClick={cancelSaveOrUpdateProduct}>Cancel</Button>
                  <Button onClick={saveOrUpdateProduct}>Save</Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
        <Box>
          <ProductAppList />
        </Box>
      </SystemDashboard>
    </Container>
  );
};

export default ProductDetail;
